// API client referidos · atribución por URL (?ref=CODE).
// Mismo patrón que `gamification-api.ts`: buildHeaders + JWT.
//
// El endpoint POST /api/me/referral/attribute lo crea otra rama. Mientras no
// exista, el atributo es fire-and-forget: cualquier error (404/red/5xx) se
// ignora silenciosamente sin romper la sesión del usuario.
//
// Owner: Carlos Beltrán · 2026-06-09.

import type { AuthIdentity } from './types';
import { isStubModeAllowed } from './env';
import { getImpersonationToken } from './impersonation';

const API_BASE = '/api';

/** localStorage key donde guardamos el código de referido capturado por URL. */
export const REFERRAL_STORAGE_KEY = 'robotea_ref';

function buildHeaders(auth: AuthIdentity | null): HeadersInit {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  // Impersonación "ver como alumno": prioridad sobre el JWT real del admin.
  // Mismo patrón que api.ts · evita filtrar datos del admin en vista-alumno.
  const impersonationToken = getImpersonationToken();
  if (impersonationToken) {
    headers['Authorization'] = `Bearer ${impersonationToken}`;
    return headers;
  }
  if (!auth) return headers;
  if (auth.mode === 'supabase' && auth.jwt) {
    headers['Authorization'] = `Bearer ${auth.jwt}`;
  } else if (auth.mode === 'stub' && isStubModeAllowed()) {
    headers['X-User-Email'] = auth.email;
  }
  return headers;
}

/**
 * Atribuye un código de referido al usuario autenticado.
 * Fire-and-forget: devuelve `true` en éxito (2xx), `false` en cualquier otro
 * caso (endpoint aún no existe, error de red, etc.). Nunca lanza.
 */
export async function attributeReferral(
  auth: AuthIdentity | null,
  code: string,
): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/me/referral/attribute`, {
      method: 'POST',
      headers: buildHeaders(auth),
      body: JSON.stringify({ code }),
    });
    return res.ok;
  } catch {
    return false;
  }
}
